import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { AuthorizePatient } from "components/new-email-order/authorize-patient";
import { Page, Spinner } from "components/ui";
import { Card, Button } from "@mui/material";
import { respondToChatNotification, fetchChatDetailsWithoutLoging } from "actions/chat";
import { useDispatch } from "react-redux";
import browserHistory from "browser-history";
import moment from "moment";
import { getBrowserTimezone, convertDateToLocal } from "utils/misc";
import { AddToCalendar } from "components/add-to-calendar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import useTranslate from "hooks/use-translate-language";

const PendingAppointment = () => {
    const location = useLocation();
    const token = queryString.parse(location.search).token;
    const chatId = queryString.parse(location.search).chat;
    const [message, setMessage] = useState("");
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [calendarModalOpen, setCalendarModalOpen] = useState(false);
    const [chatDetails, setChatDetails] = useState(null);
    const [wasAccepted, setWasAccepted] = useState(false);

    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

    const dispatch = useDispatch();
    const translate = useTranslate();
    const browserTz = `(${moment.tz(getBrowserTimezone()).format("z")})`


    const handleResponse = async (action) => {
        setIsSubmiting(true);
        try {
            const chatResponse = await dispatch(fetchChatDetailsWithoutLoging(chatId, headers));
            const chat = chatResponse?.chat;

            if (!chat) {
                console.error("Failed to fetch chat details");
                setMessage(translate("Unable to load details"));
                setIsSubmiting(false);
                return;
            }

            const result = await dispatch(respondToChatNotification(chatId, action, headers));
            setIsSubmiting(false);

            if (result && result.success) {
                const formattedAction = action === "accept" ? translate("accepted") : translate("rejected");
                const startDate = moment(chat.start_time).format('L');
                const startTime = convertDateToLocal(chat.start_time).format('LT')
                const endTime = convertDateToLocal(chat.end_time).format('LT')
                const providerName = chat.dme_details?.name || translate("your provider");

                const msg = translate("You {action} a chat with {provider} on {date} from {startTime} to {endTime} {timezone}")
                    .replace("{action}", formattedAction)
                    .replace("{provider}", providerName)
                    .replace("{date}", startDate)
                    .replace("{startTime}", startTime)
                    .replace("{endTime}", endTime)
                    .replace("{timezone}", browserTz);
                setMessage(msg);

                if (action === "accept") {
                    setChatDetails(chat);
                    setWasAccepted(true);
                }
            } else {
                console.error("Failed to respond to appointment:", result?.message);
                setMessage(translate("Could not process response"));
            }
        } catch (error) {
            console.error("Error handling appointment response:", error);
            setMessage(translate("Error occurred"));
            setIsSubmiting(false);
        }
    }

    return (
        <Page.Container style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
        }}>
            <Page.Content>
                {!message ?
                    <Card style={{ padding: '30px' }}>
                        <h3>{translate("You have a new appointment pending confirmation.")}</h3>
                        <span>{translate("Do you want to accept the appointment?")}</span>
                        <br />
                        {!isSubmiting &&
                            <>
                                <Button
                                    variant="contained"
                                    style={{ marginTop: '15px' }}
                                    color="error"
                                    onClick={() => { handleResponse("decline"); }}>
                                    {translate("No")}
                                </Button>
                                <Button
                                    variant="contained"
                                    style={{ marginLeft: '35px', marginTop: '15px' }}
                                    color="success"
                                    onClick={() => { handleResponse("accept"); }}>
                                    {translate("Yes")}
                                </Button>
                            </>
                        }
                        {isSubmiting && <Spinner style={{ marginTop: '10px' }} />}
                    </Card>
                    :
                    <Card style={{ padding: '30px' }}>
                        <h3>{message}</h3>
                        <br/>
                        {wasAccepted && chatDetails && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<CalendarTodayIcon />}
                                    onClick={() => setCalendarModalOpen(true)}
                                    style={{ marginRight: '10px', whiteSpace: 'nowrap' }}
                                    aria-label={translate("Add to Calendar")}
                                >
                                    {translate("Add to Calendar")}
                                </Button>
                                <AddToCalendar
                                    open={calendarModalOpen}
                                    closeModal={() => setCalendarModalOpen(false)}
                                    title={`${translate("Chat with")} ${chatDetails.dme_details?.name || translate("your provider")}`}
                                    description={chatDetails.description || `${translate("Chat with")} ${chatDetails.dme_details?.name || translate("your provider")} ${translate("in")} My Sleep Coach`}
                                    startDate={convertDateToLocal(chatDetails.start_time).toDate()}
                                    endDate={convertDateToLocal(chatDetails.end_time).toDate()}
                                />
                            </>
                        )}
                        <Button variant="contained" onClick={() => browserHistory.push({ pathname: "/login"})}>{translate("Click Here To View Upcoming Chats")}</Button>
                    </Card>
                }
            </Page.Content>
        </Page.Container>)
}

export default () => (
    <AuthorizePatient>
        <PendingAppointment />
    </AuthorizePatient>
);